import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { HouseholdIncomeCard } from "./HouseholdIncomeCard";
import { dec } from "@/lib/formatters";

export { HOUSEHOLD_TYPE_LABELS } from "./HouseholdIncomeCard";

const EstimateBadge = () => (
  <Badge variant="outline" className="border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase tracking-wide">Estimat</Badge>
);

const Tile = ({ label, value, sub, testId, highlight }) => (
  <div className={`p-4 border rounded-lg ${highlight ? "border-[#103766] bg-[#F3F6FA]" : "border-[#E6E9F0]"}`} data-testid={testId}>
    <p className="text-xs text-[#8AA4BF]">{label}</p>
    <p className="text-xl font-light text-[#1F2A36]">{value != null ? `${fmt(value)} kr` : "—"}</p>
    {sub && <p className="text-xs text-[#4B647F] mt-1">{sub}</p>}
  </div>
);

const vsLand = (v, l) => (v != null && l ? `Landet: ${fmt(l)} kr (${v >= l ? "+" : ""}${dec((((v - l) / l) * 100), 1)} %)` : null);

const DebtWealthCard = ({ economy, household, land }) => {
  const rows = economy || [];
  const latest = rows[rows.length - 1];
  if (!latest) return null;
  const landByYear = Object.fromEntries((land?.economy || []).map((r) => [r.year, r]));
  const la = landByYear[latest.year];
  const debtRatio = latest.avg_gjeld && latest.avg_bruttoinntekt ? latest.avg_gjeld / latest.avg_bruttoinntekt : null;
  const landDebtRatio = la?.avg_gjeld && la?.avg_bruttoinntekt ? la.avg_gjeld / la.avg_bruttoinntekt : null;
  const chartData = rows.map((r) => ({
    year: r.year,
    "Gjeld (snitt)": r.avg_gjeld,
    "Bruttoformue (snitt)": r.avg_bruttoformue,
    "Gjeld – landet": landByYear[r.year]?.avg_gjeld ?? null,
    "Bruttoformue – landet": landByYear[r.year]?.avg_bruttoformue ?? null,
  }));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="debt-wealth-section">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#103766]" />
          Gjeld og formue {latest.year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Skattepliktig gjeld, bruttoformue og bankinnskot – busette 17 år og eldre (kr)</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Per person 17 år og eldre</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Gjeld, gjennomsnitt" value={latest.avg_gjeld} sub={vsLand(latest.avg_gjeld, la?.avg_gjeld)} testId="person-avg-debt" />
            <Tile label="Gjeld, median" value={latest.median_gjeld} sub={vsLand(latest.median_gjeld, la?.median_gjeld)} testId="person-median-debt" />
            <Tile label="Bruttoformue, gjennomsnitt" value={latest.avg_bruttoformue} sub={vsLand(latest.avg_bruttoformue, la?.avg_bruttoformue)} testId="person-avg-wealth" />
            <Tile label="Bankinnskot, gjennomsnitt" value={latest.avg_bankinnskudd} sub={vsLand(latest.avg_bankinnskudd, la?.avg_bankinnskudd)} testId="person-avg-deposits" />
          </div>
          {debtRatio && (
            <p className="text-sm text-[#4B647F] mt-3" data-testid="person-debt-ratio">
              Gjeldsgrad (gjeld / bruttoinntekt): <span className="font-medium text-[#1F2A36]">{dec(debtRatio, 2)}</span>
              {landDebtRatio && <> – landet {dec(landDebtRatio, 2)}</>}
            </p>
          )}
        </div>

        {household && (
          <div data-testid="household-debt-wealth">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3 flex items-center gap-2">Per hushald <EstimateBadge /></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile label="Gjeld per hushald" value={household.avg_gjeld} testId="household-avg-debt" highlight />
              <Tile label="Bruttoformue per hushald" value={household.avg_bruttoformue} testId="household-avg-wealth" highlight />
              <Tile label="Bankinnskot per hushald" value={household.avg_bankinnskudd} testId="household-avg-deposits" highlight />
              <Tile label="Bruttoinntekt per hushald" value={household.avg_bruttoinntekt} sub={`${dec(household.adults_per_household, 2)} personar 17+ per hushald`} testId="household-avg-income" highlight />
            </div>
            <p className="text-xs text-[#B8741F] mt-2" data-testid="household-estimate-note">{household.note}</p>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Utvikling per person – kommunen mot landet (kr)</p>
          <div className="h-[280px]" data-testid="debt-wealth-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                {endringsLinjer(chartData)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${dec((v / 1000000), 1)}M`} />
                <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
                <Legend itemSorter={null} />
                <Line type="monotone" dataKey="Gjeld (snitt)" stroke="#D35E4E" strokeWidth={2} dot={{ fill: "#D35E4E", r: 3 }} connectNulls />
                <Line type="monotone" dataKey="Bruttoformue (snitt)" stroke="#103766" strokeWidth={2} dot={{ fill: "#103766", r: 3 }} connectNulls />
                <Line type="monotone" dataKey="Gjeld – landet" stroke="#D35E4E" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                <Line type="monotone" dataKey="Bruttoformue – landet" stroke="#103766" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-xs text-[#8AA4BF] flex items-center gap-1">
          Kjelde:
          <a href="https://www.ssb.no/statbank/table/05854" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#103766] hover:underline">
            SSB 05854 <ExternalLink className="w-3 h-3" />
          </a>
          – Skattepliktig formue er likningsverdiar (bustad blir verdsett lågare enn marknadsverdi). Median vs. gjennomsnitt gir eit bilete av skeivfordeling.
        </p>
      </CardContent>
    </Card>
  );
};

export const HusholdningSection = ({ data }) => {
  if (!data) return null;
  return (
    <>
      <HouseholdIncomeCard income={data.income} fylke={data.fylke} land={data.land} />
      <DebtWealthCard economy={data.economy} household={data.household} land={data.land} />
    </>
  );
};
